import {
  collection,
  doc,
  getDoc,
  setDoc,
  query,
  where,
  getDocs,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { format, startOfDay, endOfDay } from 'date-fns';

// Daily Closing interface
export interface DailyClosing {
  id?: string;
  date: Date | Timestamp;
  totalSales: number;
  transactionCount: number;
  categorySales: {[key: string]: number};
  paymentMethodSales: {[key: string]: number};
  cashDrawerAmount?: number;
  cashVariance?: number;
  notes?: string;
  glTransactionId?: string;
  isClosed: boolean;
  closedAt?: Date | Timestamp;
  closedBy?: string;
  createdAt?: Date | Timestamp;
  updatedAt?: Date | Timestamp;
}

// Save daily closing data
export const saveDailyClosing = async (closingData: Omit<DailyClosing, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  try {
    // Format date as YYYY-MM-DD for the document ID
    const dateStr = format(
      closingData.date instanceof Date ? closingData.date : closingData.date.toDate(), 
      'yyyy-MM-dd'
    );
    
    // Reference to the daily closing document
    const closingRef = doc(db, 'dailyClosings', dateStr);
    
    // Check if document already exists
    const existingDoc = await getDoc(closingRef);
    
    // Prepare data to save
    const dataToSave = {
      ...closingData,
      // Convert Date objects to Firestore Timestamps
      date: closingData.date instanceof Date 
        ? Timestamp.fromDate(closingData.date) 
        : closingData.date,
      closedAt: closingData.closedAt instanceof Date 
        ? Timestamp.fromDate(closingData.closedAt) 
        : closingData.closedAt || null,
      updatedAt: serverTimestamp()
    };
    
    if (!existingDoc.exists()) {
      // If document doesn't exist, add createdAt
      await setDoc(closingRef, {
        ...dataToSave,
        createdAt: serverTimestamp()
      });
    } else {
      // If document exists, update it
      await setDoc(closingRef, dataToSave, { merge: true });
    }
    
    return dateStr;
  } catch (error) {
    console.error('Error saving daily closing:', error);
    throw error;
  }
};

// Reopen a daily closing (set isClosed to false and remove GL transaction ID)
export const reopenDailyClosing = async (date: Date): Promise<void> => {
  try {
    const dateStr = format(date, 'yyyy-MM-dd');
    const closingRef = doc(db, 'dailyClosings', dateStr);
    const closingDoc = await getDoc(closingRef);
    
    if (!closingDoc.exists()) {
      throw new Error(`Daily closing for ${dateStr} not found`);
    }
    
    const closingData = closingDoc.data() as DailyClosing;
    
    // Update the daily closing
    await setDoc(closingRef, {
      isClosed: false,
      glTransactionId: null, // Remove GL transaction ID
      updatedAt: serverTimestamp()
    }, { merge: true });
    
  } catch (error) {
    console.error('Error reopening daily closing:', error);
    throw error;
  }
};

// Get daily closing by date
export const getDailyClosingByDate = async (date: Date): Promise<DailyClosing | null> => {
  try {
    const dateStr = format(date, 'yyyy-MM-dd');
    const closingRef = doc(db, 'dailyClosings', dateStr);
    const closingDoc = await getDoc(closingRef);
    
    if (!closingDoc.exists()) {
      return null;
    }
    
    const closingData = closingDoc.data() as DailyClosing;
    return {
      ...closingData,
      id: closingDoc.id
    };
  } catch (error) {
    console.error('Error getting daily closing:', error);
    throw error;
  }
};

// Get daily closings for a month
export const getDailyClosingsForMonth = async (year: number, month: number): Promise<DailyClosing[]> => {
  try {
    const startDate = startOfDay(new Date(year, month, 1));
    const endDate = endOfDay(new Date(year, month + 1, 0));
    
    const closingsQuery = query(
      collection(db, 'dailyClosings'),
      where('date', '>=', Timestamp.fromDate(startDate)),
      where('date', '<=', Timestamp.fromDate(endDate))
    );
    
    const querySnapshot = await getDocs(closingsQuery);
    const closings: DailyClosing[] = [];
    
    querySnapshot.forEach(doc => {
      closings.push({
        ...doc.data() as DailyClosing,
        id: doc.id
      });
    });
    
    return closings;
  } catch (error) {
    console.error('Error getting daily closings for month:', error);
    throw error;
  }
};

// Update daily closing with GL transaction ID
export const updateDailyClosingWithGLTransaction = async (date: Date, glTransactionId: string): Promise<void> => {
  try {
    const dateStr = format(date, 'yyyy-MM-dd');
    const closingRef = doc(db, 'dailyClosings', dateStr);
    
    await setDoc(closingRef, {
      glTransactionId,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error updating daily closing with GL transaction:', error);
    throw error;
  }
};
